import React, { useState } from "react";
import { Link, RouteComponentProps } from "@reach/router";
import { useForm } from "react-hook-form";
import { Button } from "@components/ui/Button";
import { Input } from "@components/ui/Input";
import { Card, CardBody } from "@components/ui/Card/Card";
import { forgotPasswordPath, signUpPath } from "@routes";

export type SignInFormData = {
  email: string;
  password: string;
};

type SignInProps = {
  email?: string;
  onSignIn: (data: SignInFormData) => Promise<void>;
  hideSignUp?: boolean;
} & RouteComponentProps;

export function SignIn({ email, onSignIn, hideSignUp = false }: SignInProps) {
  const [error, setError] = useState<string | null>();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SignInFormData>({
    mode: "onBlur",
    defaultValues: {
      email: email || "",
    },
  });

  const onSubmit = async (data: SignInFormData) => {
    setError(null);

    try {
      await onSignIn(data);
      reset();
    } catch (error) {
      if (error.message) {
        setError(error.message);
      } else {
        // TODO report this error somewhere
        setError("An unknown error occurred, please reach out to support.");
      }
    }
  };

  return (
    <Card title="Sign in">
      <CardBody>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-2">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <div className="mt-1">
                <Input
                  type="email"
                  {...register("email", {
                    required: {
                      value: true,
                      message: "Email is required",
                    },
                  })}
                  error={errors.email?.message}
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <div className="mt-1">
                <Input
                  type="password"
                  {...register("password", {
                    required: {
                      value: true,
                      message: "Password is required",
                    },
                  })}
                  error={errors.password?.message}
                />
              </div>
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <div>
              <Button size="lg" type="submit" disabled={false}>
                Sign in
              </Button>
            </div>
            {!hideSignUp && (
              <div className="ml-6 text-sm">
                <div>
                  No account?{" "}
                  <Link to={signUpPath} className="link">
                    Sign up
                  </Link>
                </div>
                <div>
                  Forget your password?{" "}
                  <Link to={forgotPasswordPath} className="link">
                    Reset password
                  </Link>
                </div>
              </div>
            )}
          </div>
        </form>
        {error && <div className="mt-6 text-red-600">{error}</div>}
      </CardBody>
    </Card>
  );
}
